import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { ChangeEvent, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { uploadOperationalSignature } from "../services/dashboard";

type SignatureUploadFieldProps = {
  label: string;
  module: string;
  referenceNumber?: string | null;
  fieldName: string;
  value?: string | null;
  disabled?: boolean;
  helperText?: string;
  error?: string;
  onUploaded: (fileKey: string) => void;
};

const deriveDisplayName = (value?: string | null) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  const parts = normalized.split("/");
  return parts[parts.length - 1] || "Uploaded signature";
};

export default function SignatureUploadField({
  label,
  module,
  referenceNumber,
  fieldName,
  value,
  disabled = false,
  helperText,
  error,
  onUploaded,
}: SignatureUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [recentFileName, setRecentFileName] = useState("");

  const displayValue = useMemo(
    () => recentFileName || deriveDisplayName(value),
    [recentFileName, value],
  );

  const handlePick = () => {
    if (disabled || uploading) return;
    inputRef.current?.click();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = "";

    if (!selectedFile) return;
    if (!referenceNumber) {
      toast.error("Save or load the record first before uploading a signature.");
      return;
    }

    try {
      setUploading(true);
      const response = await uploadOperationalSignature(
        module,
        referenceNumber,
        fieldName,
        selectedFile,
      );

      if (response?.status === 200 && response?.data?.fileKey) {
        setRecentFileName(selectedFile.name);
        onUploaded(response.data.fileKey);
        return;
      }

      toast.error(response?.data?.message || "Unable to upload signature");
    } catch (uploadError) {
      console.error("Signature upload failed", uploadError);
      toast.error("Unable to upload signature");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.85, minWidth: 0, width: "100%" }}>
      <Box
        sx={{
          border: `1px solid ${error ? "#ef4444" : "#e5e7eb"}`,
          borderRadius: "18px",
          backgroundColor: disabled ? "#f8fafc" : "#ffffff",
          px: 1.5,
          py: 1.35,
          transition: "all 0.18s ease",
          "&:hover": disabled
            ? {}
            : {
                borderColor: "#fdba74",
                boxShadow: "0 10px 24px rgba(249,115,22,0.08)",
              },
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: "#94a3b8",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          {label}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: { xs: "stretch", lg: "center" },
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "nowrap",
            minWidth: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, minWidth: 0, flex: 1, width: "100%" }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "12px",
                bgcolor: displayValue ? "#fff7ed" : "#f8fafc",
                border: `1px solid ${displayValue ? "#fed7aa" : "#e5e7eb"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: displayValue ? "#f97316" : "#94a3b8",
                flexShrink: 0,
              }}
            >
              {displayValue ? (
                <CheckCircleRoundedIcon fontSize="small" />
              ) : (
                <DescriptionOutlinedIcon fontSize="small" />
              )}
            </Box>

            <Box sx={{ minWidth: 0, width: "100%" }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#111827",
                  lineHeight: 1.25,
                  whiteSpace: { xs: "normal", lg: "nowrap" },
                  overflow: "hidden",
                  textOverflow: { xs: "clip", lg: "ellipsis" },
                  wordBreak: "break-word",
                }}
              >
                {displayValue || "No signature file uploaded"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#94a3b8",
                  mt: 0.25,
                }}
              >
                {displayValue ? "File attached and ready to save" : "Upload image or PDF"}
              </Typography>
            </Box>
          </Box>

          <Button
            type="button"
            variant="contained"
            onClick={handlePick}
            disabled={disabled || uploading}
            startIcon={uploading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <CloudUploadOutlinedIcon />}
            sx={{
              width: { xs: "100%", lg: "auto" },
              minWidth: { xs: "100%", lg: 136 },
              height: 42,
              px: 2.25,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #fb923c 0%, #f97316 100%)",
              color: "#fff",
              textTransform: "none",
              fontWeight: 800,
              whiteSpace: "nowrap",
              alignSelf: { xs: "stretch", lg: "center" },
              boxShadow: "0 10px 22px rgba(249,115,22,0.22)",
              "&:hover": {
                background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
                boxShadow: "0 12px 24px rgba(249,115,22,0.26)",
              },
              "&.Mui-disabled": {
                background: "#cbd5e1",
                color: "#fff",
                boxShadow: "none",
              },
            }}
          >
            {uploading ? "Uploading" : displayValue ? "Replace" : "Upload"}
          </Button>
        </Box>
      </Box>
      {error ? (
        <Typography sx={{ fontSize: 12, color: "#dc2626", pl: 0.5, fontWeight: 600 }}>
          {error}
        </Typography>
      ) : helperText ? (
        <Typography sx={{ fontSize: 12, color: "#94a3b8", pl: 0.5 }}>
          {helperText}
        </Typography>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.pdf"
        hidden
        onChange={handleFileChange}
      />
    </Box>
  );
}
